import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { change_password } from "../../../../api/calls/user/change_password";
import { clearPasswordResetOtp, clearRegistrationOtp, sendPasswordResetOtp, sendRegistrationOtp, verifyPasswordResetOtp, verifyRegistrationOtp, } from "../../../../reducers/slices/otp";
import { setMainModal } from "../../../../reducers/slices/main";
import { setUser, sign_in_thunk, sign_up_thunk } from "../../../../reducers/slices/user";
function thrownMessage(error, fallback) {
    return typeof error === "string" ? error : error instanceof Error ? error.message : fallback;
}
export function useAuthState() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.userState);
    const otp = useSelector((state) => state.otpState);
    const showError = useCallback((title, error, fallback) => {
        dispatch(setMainModal({ title, messages: [thrownMessage(error, fallback)] }));
    }, [dispatch]);
    const handleLogin = useCallback(async (data) => {
        dispatch(setUser({ ...user, user: { ...user.user, ...data } }));
        await dispatch(sign_in_thunk());
    }, [dispatch, user]);
    const handleRegister = useCallback(async (data) => {
        const { delivery_method, show_customer_code, ...registrationUser } = data;
        dispatch(setUser({ ...user, user: { ...user.user, ...registrationUser } }));
        try {
            await dispatch(sendRegistrationOtp({ values: data, deliveryMethod: delivery_method })).unwrap();
            navigate("/auth/register-otp");
        }
        catch (error) {
            showError("Verification code not sent", error, "Please try again.");
        }
    }, [dispatch, navigate, showError, user]);
    const handleRegistrationOTP = useCallback(async ({ pin }) => {
        const pendingRegistration = otp.registration;
        if (!pendingRegistration) {
            navigate("/auth/register", { replace: true });
            return;
        }
        try {
            await dispatch(verifyRegistrationOtp({ cellNumber: pendingRegistration.cellNumber, otp: pin })).unwrap();
            const { delivery_method, show_customer_code, ...registrationUser } = pendingRegistration.values;
            dispatch(setUser({ ...user, user: { ...user.user, ...registrationUser } }));
            const response = (await dispatch(sign_up_thunk()).unwrap());
            if (response.success === true) {
                dispatch(clearRegistrationOtp());
                navigate("/home");
            }
        }
        catch (error) {
            showError("Verification failed", error, "The verification code is invalid or expired.");
        }
    }, [dispatch, navigate, otp.registration, showError, user]);
    const handleForgotPassword = useCallback(async ({ email, delivery_method }) => {
        try {
            const { context } = await dispatch(sendPasswordResetOtp({ email, deliveryMethod: delivery_method })).unwrap();
            dispatch(setUser({ ...user, user: { ...user.user, email: context.email, cell_number: context.accountCellNumber } }));
            navigate("/auth/otp");
        }
        catch (error) {
            showError("Verification code not sent", error, "Please try again.");
        }
    }, [dispatch, navigate, showError, user]);
    const handleOTP = useCallback(async ({ pin }) => {
        const passwordReset = otp.passwordReset;
        if (!passwordReset) {
            navigate("/auth/forgot-password", { replace: true });
            return;
        }
        try {
            await dispatch(verifyPasswordResetOtp({
                accountCellNumber: passwordReset.accountCellNumber,
                verificationCellNumber: passwordReset.verificationCellNumber,
                otp: pin,
            })).unwrap();
            navigate("/auth/reset-password");
        }
        catch (error) {
            showError("Verification failed", error, "The verification code is invalid or expired.");
        }
    }, [dispatch, navigate, otp.passwordReset, showError]);
    const handleResetPassword = useCallback(async (data) => {
        const passwordReset = otp.passwordReset;
        if (!passwordReset?.verifiedOtp) {
            navigate("/auth/forgot-password", { replace: true });
            return;
        }
        const response = (await change_password({
            otp: passwordReset.verifiedOtp,
            cell_number: passwordReset.accountCellNumber,
            new_password: data.password,
        }));
        if (response.success === true) {
            dispatch(clearPasswordResetOtp());
            navigate("/auth/login");
            dispatch(setMainModal({
                title: "Password Reset Completed",
                messages: [response.message ?? "Your password has been updated.", "Please sign in with your new password."],
            }));
            return;
        }
        showError("Failed to reset your password", response.message, "Please try again.");
    }, [dispatch, navigate, otp.passwordReset, showError]);
    const resendRegistrationOtp = useCallback(async () => {
        if (!otp.registration) {
            navigate("/auth/register", { replace: true });
            return;
        }
        try {
            await dispatch(sendRegistrationOtp({
                values: otp.registration.values,
                deliveryMethod: otp.registration.deliveryMethod,
            })).unwrap();
        }
        catch (error) {
            showError("Verification code not sent", error, "Please try again.");
        }
    }, [dispatch, navigate, otp.registration, showError]);
    const resendPasswordResetOtp = useCallback(async () => {
        if (!otp.passwordReset) {
            navigate("/auth/forgot-password", { replace: true });
            return;
        }
        try {
            await dispatch(sendPasswordResetOtp({
                email: otp.passwordReset.email,
                deliveryMethod: otp.passwordReset.deliveryMethod,
            })).unwrap();
        }
        catch (error) {
            showError("Verification code not sent", error, "Please try again.");
        }
    }, [dispatch, navigate, otp.passwordReset, showError]);
    return useMemo(() => ({
        handleForgotPassword,
        handleLogin,
        handleOTP,
        handleRegister,
        handleRegistrationOTP,
        handleResetPassword,
        isSendingOtp: otp.isSending,
        isVerifyingOtp: otp.isVerifying,
        isCompletingRegistration: otp.isVerifying || user.is_loading,
        passwordReset: otp.passwordReset,
        registration: otp.registration,
        resendAvailableAt: otp.resendAvailableAt,
        resendPasswordResetOtp,
        resendRegistrationOtp,
    }), [
        handleForgotPassword,
        handleLogin,
        handleOTP,
        handleRegister,
        handleRegistrationOTP,
        handleResetPassword,
        otp.isSending,
        otp.isVerifying,
        otp.passwordReset,
        otp.registration,
        otp.resendAvailableAt,
        resendPasswordResetOtp,
        resendRegistrationOtp,
        user.is_loading,
    ]);
}
